/* eslint-disable @typescript-eslint/no-explicit-any */
// Server-side (SSR/RSC) fetches must use the internal Docker service name.
// Browser fetches use the public URL that the end-user's browser can reach.
const API_BASE =
  typeof window === 'undefined'
    ? process.env.API_URL || process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000'
    : process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000';

interface FetchOptions extends RequestInit {
  token?: string;
}

export async function api<T>(endpoint: string, options: FetchOptions = {}): Promise<T> {
  const { token, headers: customHeaders, ...rest } = options;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((customHeaders as Record<string, string>) || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}/api${endpoint}`, {
    headers,
    ...rest,
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: 'An error occurred' }));
    throw new Error(error.message || `API Error: ${res.status}`);
  }

  return res.json();
}

// Public API helpers (server-side, no auth)
export const publicApi = {
  getProperties: (params?: Record<string, any>) => {
    const qs = params ? '?' + new URLSearchParams(params as any).toString() : '';
    return api(`/properties${qs}`, { cache: 'no-store' } as any);
  },
  getFeaturedProperties: (limit = 8) =>
    api(`/properties/featured?limit=${limit}`, { next: { revalidate: 60 } } as any),
  getPropertyBySlug: (slug: string) =>
    api(`/properties/slug/${slug}`, { cache: 'no-store' } as any),
  getRegions: () =>
    api('/regions', { next: { revalidate: 3600 } } as any),
  getCities: (regionId?: string) =>
    api(`/cities${regionId ? `?regionId=${regionId}` : ''}`, { next: { revalidate: 3600 } } as any),
  getDistricts: (cityId?: string) =>
    api(`/districts${cityId ? `?cityId=${cityId}` : ''}`, { next: { revalidate: 3600 } } as any),
  getPopularCities: () =>
    api('/cities/popular', { next: { revalidate: 3600 } } as any),
  searchCities: (q: string) =>
    api(`/cities/search?q=${q}`),
  getAgencies: (params?: Record<string, any>) => {
    const qs = params ? '?' + new URLSearchParams(params as any).toString() : '';
    return api(`/agencies${qs}`, { next: { revalidate: 300 } } as any);
  },
  getAgencyBySlug: (slug: string) =>
    api(`/agencies/slug/${slug}`, { next: { revalidate: 300 } } as any),
  getPacks: () =>
    api('/packs', { next: { revalidate: 3600 } } as any),
  getArticles: (params?: Record<string, any>) => {
    const qs = params ? '?' + new URLSearchParams(params as any).toString() : '';
    return api(`/blog/articles${qs}`, { next: { revalidate: 300 } } as any);
  },
  getArticleBySlug: (slug: string) =>
    api(`/blog/articles/slug/${slug}`, { cache: 'no-store' } as any),
  getCategories: () =>
    api('/categories', { next: { revalidate: 3600 } } as any),
  search: (params: Record<string, any>) => {
    const qs = new URLSearchParams(params as any).toString();
    return api(`/search?${qs}`, { cache: 'no-store' } as any);
  },
  getSeoPage: (route: string) =>
    api(`/seo/page?route=${encodeURIComponent(route)}`),
  getBlogCategories: () =>
    api('/blog/categories/flat', { next: { revalidate: 3600 } } as any),
  getArticleReactions: (articleId: string) =>
    api(`/blog/articles/${articleId}/reactions`, { cache: 'no-store' } as any),
  getArticleComments: (articleId: string) =>
    api(`/blog/articles/${articleId}/comments`, { cache: 'no-store' } as any),
  getSiteContact: () =>
    api<any>('/site-settings/contact', { next: { revalidate: 300 } } as any),
  getPublicStats: () =>
    api<any>('/site-settings/public-stats', { next: { revalidate: 300 } } as any),
};

// ─── Browser-only blog interaction API ────────────────────────────────────────
// Used in 'use client' components for reactions, comments, shares.
export const blogClientApi = {
  getReactions: (articleId: string) =>
    api(`/blog/articles/${articleId}/reactions`),
  getComments: (articleId: string) =>
    api(`/blog/articles/${articleId}/comments`),
  recordView: (articleId: string, fingerprint: string, token?: string) =>
    api(`/blog/articles/${articleId}/view`, {
      method: 'POST',
      headers: { 'X-Fingerprint': fingerprint },
      ...(token ? { token } : {}),
    }),
  toggleReaction: (articleId: string, reaction: string, token: string) =>
    api(`/blog/articles/${articleId}/like?reaction=${encodeURIComponent(reaction)}`, {
      method: 'POST',
      token,
    }),
  postComment: (
    articleId: string,
    data: { content: string; parentId?: string },
    token: string,
  ) =>
    api(`/blog/articles/${articleId}/comments`, {
      method: 'POST',
      body: JSON.stringify(data),
      token,
    }),
  shareArticle: (articleId: string, platform: string, token: string) =>
    api(`/blog/articles/${articleId}/share`, {
      method: 'POST',
      body: JSON.stringify({ platform }),
      token,
    }),
};

// ─── Auth API ───────────────────────────────────────────────────────────────
export const authApi = {
  login: (email: string, password: string) =>
    api<{ accessToken: string; refreshToken: string; user: any }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  register: (data: {
    email: string; password: string;
    firstName: string; lastName: string;
    phone?: string; role?: string;
    ownerTypes?: string[];
  }) =>
    api<{ accessToken: string; refreshToken: string; user: any }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  refresh: (refreshToken: string) =>
    api<{ accessToken: string; refreshToken: string }>('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    }),
  forgotPassword: (email: string) =>
    api<{ message: string }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  resetPassword: (token: string, password: string) =>
    api<{ message: string }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),
  logout: (accessToken: string) =>
    api<void>('/auth/logout', { method: 'POST', token: accessToken }),
  getProfile: (accessToken: string) =>
    api<any>('/users/profile', { token: accessToken }),
  updateProfile: (accessToken: string, data: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    emailNotifTransactions?: boolean;
    emailNotifMessages?: boolean;
    emailNotifAlerts?: boolean;
    emailNotifNewsletter?: boolean;
  }) =>
    api<any>('/users/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
      token: accessToken,
    }),
  changePassword: (accessToken: string, data: { currentPassword: string; newPassword: string }) =>
    api<{ message: string }>('/users/profile/password', {
      method: 'PATCH',
      body: JSON.stringify(data),
      token: accessToken,
    }),
};

// ─── Owners / Agencies onboarding API ───────────────────────────────────────
export const ownersApi = {
  createSelf: (
    accessToken: string,
    data: {
      firstName: string;
      lastName: string;
      phone?: string;
      email?: string;
      cin?: string;
      passport?: string;
      address?: string;
      notes?: string;
      type?: string;
      ownerTypes?: Array<'vendeur' | 'bailleur' | 'mandataire'>;
      companyName?: string;
      cityId?: string;
    },
  ) =>
    api('/owners/register', {
      method: 'POST',
      body: JSON.stringify(data),
      token: accessToken,
    }),
};

export const agenciesApi = {
  createSelf: (
    accessToken: string,
    data: {
      name: string;
      managerName: string;
      managerCin: string;
      ice: string;
      ifNumber?: string;
      rc: string;
      cnss?: string;
      phone?: string;
      email?: string;
      website?: string;
      address?: string;
      description?: string;
      cityId?: string;
    },
  ) =>
    api('/agencies/register', {
      method: 'POST',
      body: JSON.stringify(data),
      token: accessToken,
    }),
};

// ─── Messages API ─────────────────────────────────────────────────────────────
export const messagesApi = {
  /** Create a new conversation (optional auth — guests provide name+email). */
  createConversation: (
    data: {
      propertyId: string;
      body: string;
      subject?: string;
      guestName?: string;
      guestEmail?: string;
      guestPhone?: string;
    },
    token?: string,
  ) =>
    api<any>('/messages/conversations', {
      method: 'POST',
      body: JSON.stringify(data),
      ...(token ? { token } : {}),
    }),

  /** List the authenticated user's own conversations. */
  getMyConversations: (token: string, page = 1, limit = 20) =>
    api<any>(`/messages/conversations/my?page=${page}&limit=${limit}`, { token }),

  /** Get a single conversation with full message thread. */
  getConversation: (id: string, token: string) =>
    api<any>(`/messages/conversations/${id}`, { token }),

  /** Send a reply inside an existing conversation. */
  sendMessage: (conversationId: string, body: string, token: string) =>
    api<any>(`/messages/conversations/${conversationId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ body }),
      token,
    }),

  /** Mark all admin messages in this conversation as read (user side). */
  markReadByUser: (conversationId: string, token: string) =>
    api<any>(`/messages/conversations/${conversationId}/user-read`, {
      method: 'PATCH',
      token,
    }),

  /** Send a public general contact message (no auth required). */
  sendGeneralContact: (data: {
    name: string;
    email: string;
    phone?: string;
    subject?: string;
    body: string;
  }) =>
    api<any>('/messages/contact', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// ─── Buyer Requests API ───────────────────────────────────────────────────────
export const buyerRequestsApi = {
  /** Submit a new buyer request (anonymous or authenticated). */
  create: (
    data: {
      fullName: string;
      email: string;
      phone?: string;
      propertyTypes: string[];
      transactionType: 'sale' | 'rent';
      cityIds?: string[];
      districtIds?: string[];
      budgetMin?: number;
      budgetMax?: number;
      surfaceMin?: number;
      bedroomsMin?: number;
      timeline?: string;
      financing?: string;
      notes?: string;
    },
    token?: string,
  ) =>
    api<any>('/buyer-requests', {
      method: 'POST',
      body: JSON.stringify(data),
      ...(token ? { token } : {}),
    }),

  /** List the authenticated user's own requests. */
  getMine: (token: string) =>
    api<any[]>('/buyer-requests/my', { token }),

  /** Get one request with full status timeline. */
  getOne: (id: string, token: string) =>
    api<any>(`/buyer-requests/my/${id}`, { token }),
};

// ─── Custom (Personalised) Requests API ──────────────────────────────────────
export const customRequestsApi = {
  /** Submit a personalised property search request (no auth required). */
  create: (data: {
    fullName: string;
    email: string;
    phone?: string;
    intent: 'buy' | 'rent';
    region?: string;
    city?: string;
    district?: string;
    address?: string;
    message: string;
  }) =>
    api<any>('/custom-requests', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// ─── Listing (property creation by owner) ─────────────────────────────────────
export const listingApi = {
  /** Submit a new property listing (requires authentication). */
  create: (
    data: {
      title: string;
      description: string;
      price: number;
      type: string;
      transactionType: 'sale' | 'rent';
      surface?: number;
      bedrooms?: number;
      bathrooms?: number;
      rooms?: number;
      floor?: number;
      yearBuilt?: number;
      condition?: string;
      furnished?: boolean;
      address?: string;
      cityId?: string;
      regionId?: string;
      districtId?: string;
      features?: Record<string, boolean>;
      status?: string;
      packId?: string;
    },
    token: string,
  ) =>
    api<any>('/properties', {
      method: 'POST',
      body: JSON.stringify({ ...data, status: data.status ?? 'pending' }),
      token,
    }),

  /** Get the authenticated owner's own listings. */
  getMine: (token: string, params?: Record<string, any>) => {
    const qs = params ? '?' + new URLSearchParams(params as any).toString() : '';
    return api<any>(`/properties/my${qs}`, { token });
  },

  /** Get a single property by id (uses findOne which remaps media URLs). */
  getById: (id: string, token?: string) =>
    api<any>(`/properties/${id}`, token ? { token } : {}),

  /** Update an existing property listing. */
  update: (
    id: string,
    data: Record<string, any>,
    token: string,
  ) =>
    api<any>(`/properties/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
      token,
    }),
};

